/**
 * المرحلة 1.3 (M03) — «احجز بالميعاد»: the day and the hour first, then the
 * doctors free in it, then the channel.
 *
 * GET /api/bookings/match answers from the same slot board the clinic books
 * from (closed days, extra hours, full slots, times already gone), within
 * the specialty — and its close ones when the scope's matchScope says so,
 * listed after. Choosing a doctor opens the booking page with that clinic,
 * day and time already chosen; the patient finishes there as with any
 * booking (the visit type, a voucher, the deposit).
 *
 * Closed while bookingJourneys.journeys is doctor-first, the default: the
 * screen says so and points at the doctors' list.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Avatar, Badge, Button, EmptyState, Icon, PageHeader, Panel, Select, Spinner } from '../../components/common';
import { bookingApi } from '../../api/endpoints';
import { useData } from '../../context/DataContext';
import { categoryLabel, categoryName } from '../../domain/catalog';
import { formatCurrency } from '../../domain/pricing';
import { formatTime12 } from '../../domain/dates';
import { addDays, hospitalClock, slotFirstOpen } from '../../domain/discovery';
import { useI18n } from '../../i18n/I18nProvider';
import { ModeBadge, RatingLine, dayLabel, forwardIcon, useBookingPolicy } from '../discovery/parts';

const DAYS = 14;
const HOURS = Array.from({ length: 16 }, (_, i) => i + 8); // 08:00 … 23:00

export function BookBySlot() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { categories } = useData();
  const policy = useBookingPolicy();
  const clock = useMemo(() => hospitalClock(), []);

  const [categoryId, setCategoryId] = useState(search.get('categoryId') || '');
  const [date, setDate] = useState(clock.today);
  const [hour, setHour] = useState(null);
  const [mode, setMode] = useState('in_person');
  const [chosen, setChosen] = useState(null);
  const [result, setResult] = useState({ loading: false, error: null, data: null });

  const days = useMemo(() => Array.from({ length: DAYS }, (_, i) => addDays(clock.today, i)), [clock.today]);
  // Today's hours already gone are not offered.
  const hours = HOURS.filter((h) => date !== clock.today || h * 60 + 59 > clock.minute);

  useEffect(() => {
    if (!policy.modes.includes(mode)) setMode('in_person');
  }, [policy.modes, mode]);

  useEffect(() => {
    setChosen(null);
    if (hour === null || !categoryId) {
      setResult({ loading: false, error: null, data: null });
      return undefined;
    }
    let live = true;
    setResult({ loading: true, error: null, data: null });
    bookingApi
      .match({ categoryId, date, time: `${String(hour).padStart(2, '0')}:00`, mode })
      .then((data) => live && setResult({ loading: false, error: null, data }))
      .catch((error) => live && setResult({ loading: false, error, data: null }));
    return () => {
      live = false;
    };
  }, [categoryId, date, hour, mode]);

  if (!slotFirstOpen(policy)) {
    return (
      <div className="space-y-6">
        <PageHeader title={t('bySlot.title')} icon="calendar-search" />
        <Panel>
          <EmptyState
            icon="calendar-x"
            title={t('bySlot.closedTitle')}
            message={t('bySlot.closedMessage')}
            action={<Link to="/patient/doctors" className="btn-primary">{t('bySlot.toDoctors')}</Link>}
          />
        </Panel>
      </div>
    );
  }

  const step = !categoryId || hour === null ? 1 : chosen ? 3 : 2;
  const doctors = result.data?.doctors || [];

  const go = () => {
    const params = new URLSearchParams({ date, time: chosen.time, clinicId: String(chosen.clinicId), mode });
    navigate(`/patient/book/${chosen.doctorId}?${params.toString()}`);
  };

  return (
    <div className="space-y-6" data-testid="book-by-slot">
      <PageHeader title={t('bySlot.title')} subtitle={t('bySlot.subtitle')} icon="calendar-search" />

      <ol className="steps-bar" aria-label={t('bySlot.steps')}>
        {['when', 'who', 'confirm'].map((id, index) => (
          <li key={id} className={`steps-bar__step ${step === index + 1 ? 'is-on' : step > index + 1 ? 'is-done' : ''}`}>
            <Icon name={step > index + 1 ? 'check' : 'circle'} size={12} />
            {t(`bySlot.step.${id}`)}
          </li>
        ))}
      </ol>

      <Panel>
        <h3 className="mb-3 flex items-center gap-2 text-base font-bold text-white">
          <Icon name="calendar-days" size={18} className="text-emerald-400" />
          {t('bySlot.when')}
        </h3>

        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} aria-label={t('bySlot.specialty')} data-testid="slot-specialty">
            <option value="">{t('bySlot.specialty')}</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {categoryLabel(c, lang)}
              </option>
            ))}
          </Select>
          {policy.modes.length > 1 && (
            <Select value={mode} onChange={(e) => setMode(e.target.value)} aria-label={t('discovery.mode.label')}>
              {policy.modes.map((m) => (
                <option key={m} value={m}>
                  {t(`discovery.mode.${m}`)}
                </option>
              ))}
            </Select>
          )}
        </div>

        <div className="day-strip" role="group" aria-label={t('bySlot.day')}>
          {days.map((d) => (
            <button key={d} type="button" className={`day-btn ${date === d ? 'is-on' : ''}`} onClick={() => setDate(d)} aria-pressed={date === d} data-testid={`day-${d}`}>
              <span>{d === clock.today ? t('bySlot.today') : dayLabel(d, lang, { weekday: 'short' })}</span>
              <strong>{Number(d.slice(8))}</strong>
              <span>{dayLabel(d, lang, { month: 'short' })}</span>
            </button>
          ))}
        </div>

        <p className="mb-2 mt-4 text-xs font-bold text-slate-300">{t('bySlot.hour')}</p>
        <div className="slot-grid" role="group" aria-label={t('bySlot.hour')}>
          {hours.map((h) => (
            <button
              key={h}
              type="button"
              className={`slot-btn ${hour === h ? 'is-on' : ''}`}
              onClick={() => setHour(h)}
              aria-pressed={hour === h}
              data-testid={`hour-${h}`}
            >
              {formatTime12(`${String(h).padStart(2, '0')}:00`)}
            </button>
          ))}
        </div>
      </Panel>

      <Panel>
        <h3 className="mb-3 flex items-center gap-2 text-base font-bold text-white">
          <Icon name="stethoscope" size={18} className="text-cyan-400" />
          {t('bySlot.who')}
        </h3>
        {!categoryId || hour === null ? (
          <p className="text-sm text-slate-400">{t('bySlot.pickFirst')}</p>
        ) : result.loading ? (
          <Spinner variant="inline" label={t('bySlot.searching')} />
        ) : result.error ? (
          <p className="reason-note">
            <Icon name="alert-triangle" size={14} className="mt-0.5 shrink-0" />
            {result.error.body?.code === 'JOURNEY_CLOSED' ? t('bySlot.closedMessage') : result.error.message}
          </p>
        ) : doctors.length === 0 ? (
          <EmptyState icon="user-x" title={t('bySlot.noneTitle')} message={t('bySlot.noneMessage')} />
        ) : (
          <div className="space-y-2" data-testid="match-list">
            {doctors.map((d) => {
              const on = chosen && chosen.doctorId === d.doctorId;
              return (
                <button
                  key={d.doctorId}
                  type="button"
                  className={`match-row w-full text-start ${on ? 'is-on' : ''}`}
                  onClick={() => setChosen(d)}
                  aria-pressed={on}
                  data-testid="match-row"
                  data-doctor={d.doctorId}
                >
                  <Avatar src={d.image} name={d.name} size={44} />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-bold text-white">{d.name}</span>
                    <span className="block truncate text-xs text-slate-400">
                      {d.title || categoryName(d.categoryId, categories, lang)} · {d.clinicName}
                    </span>
                    <span className="mt-1 flex flex-wrap items-center gap-2">
                      <RatingLine avg={d.ratingAvg} count={d.ratingCount} />
                      {d.related && <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-300">{t('bySlot.related', { name: categoryName(d.categoryId, categories, lang) })}</Badge>}
                      {d.price !== null && <span className="text-xs font-bold text-emerald-300">{formatCurrency(d.price)}</span>}
                    </span>
                  </span>
                  <span className="match-row__time">{formatTime12(d.time)}</span>
                </button>
              );
            })}
          </div>
        )}
      </Panel>

      {chosen && (
        <Panel className="border border-cyan-500/30">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              <p className="text-sm font-bold text-white">{t('bySlot.confirmTitle', { name: chosen.name })}</p>
              <p className="text-xs text-slate-300">
                {dayLabel(date, lang)} · {formatTime12(chosen.time)} · {chosen.clinicName}
              </p>
              <ModeBadge mode={mode} />
            </div>
            <Button icon={forwardIcon(lang)} onClick={go} data-testid="match-continue" className="shrink-0">
              {t('bySlot.continue')}
            </Button>
          </div>
        </Panel>
      )}
    </div>
  );
}

export default BookBySlot;
